import { useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { handleSaveImage } from '../utils/handleSaveImage';
function normalizeTransformation(t: any) {
    if (!t) return t;

    const clone = { ...t };

    if (clone.crop) {
        clone.crop = { ...clone.crop };
        delete clone.crop.zoom;
    }

    delete clone.x;
    delete clone.y;

    return clone;
}

export const useDebouncedSaveImage = (
    {
        activePrompt,
        selectedImage,
        dispatch,
        newImageWithEffects,
    }: {
        activePrompt: string | null;
        selectedImage: any;
        dispatch: any;
        newImageWithEffects: any;
    },
    delay = 1000,
) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fileRef = useRef<File | null>(null);
    fileRef.current = newImageWithEffects;

    const prevRef = useRef<{
        id: string | null;
        visual: any;
        canvas: any;
        effects: any;
        transformation: any;
    }>({ id: null, visual: null, canvas: null, effects: null, transformation: null });

    useEffect(() => {
        const img = selectedImage?.image;
        if (!img) return;

        const sameId = img.id === prevRef.current.id;
        const visualCh = !isEqual(img.visual_settings, prevRef.current.visual);
        const canvasCh = !isEqual(img.canvas_color, prevRef.current.canvas);
        const effectCh = !isEqual(img.visual_effects, prevRef.current.effects);
        const transformationCh = !isEqual(
            normalizeTransformation(img.transformation),
            normalizeTransformation(prevRef.current.transformation),
        );

        prevRef.current = {
            id: img.id,
            visual: img.visual_settings,
            canvas: img.canvas_color,
            effects: img.visual_effects,
            transformation: img.transformation,
        };

        if (!sameId) return;

        if (!visualCh && !canvasCh && !effectCh && !transformationCh) return;

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            handleSaveImage({
                activePrompt,
                selectedImage,
                dispatch,
                newImageWithEffects: fileRef.current,
            });
        }, delay);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [
        selectedImage.image?.visual_settings,
        selectedImage.image?.canvas_color,
        selectedImage.image?.visual_effects,
        selectedImage.image?.transformation,
        delay,
    ]);
};
